import { type RefObject, useEffect, useRef, useState } from 'react';

const DEFAULT_INTERSECTION_OPTIONS: IntersectionObserverInit = {
  threshold: [0, 1],
  rootMargin: '0px',
};

export type UseIntersectionToggleOptions = {
  onChange?: (isActive: boolean) => void;
  intersectionOptions?: IntersectionObserverInit;
};

/**
 * Observes a target element and returns a boolean that flips when the predicate is met or not.
 * Initial state is false until the first intersection callback runs.
 * Useful for scroll-based UI toggles (e.g. "has user scrolled past this element?").
 *
 * @param targetRef - RefObject for the element to observe (e.g. from useRef<HTMLElement>(null))
 * @param predicate - Called with each intersection entry; return true when the predicate is met
 * @param options - Optional onChange and IntersectionObserver options
 * @returns Whether the predicate is currently true
 */
export function useIntersectionToggle(
  targetRef: RefObject<HTMLElement | null>,
  predicate: (entry: IntersectionObserverEntry) => boolean,
  options?: UseIntersectionToggleOptions,
): boolean {
  const { onChange, intersectionOptions = DEFAULT_INTERSECTION_OPTIONS } = options ?? {};
  const [isActive, setIsActive] = useState(false);
  const previousRef = useRef(false);

  useEffect(() => {
    if (!targetRef.current) {
      return;
    }
    const target = targetRef.current;

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry) {
        return;
      }
      const active = predicate(entry);
      if (active !== previousRef.current) {
        previousRef.current = active;
        setIsActive(active);
        onChange?.(active);
      }
    }, intersectionOptions);

    observer.observe(target);
    return () => observer.disconnect();
  }, [targetRef, predicate, onChange, intersectionOptions]);

  return isActive;
}
